import React, { useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, ArrowRight, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const emailSchema = z.string().email("Voer een geldig emailadres in");

const NewsletterSignup = forwardRef<HTMLFormElement, object>((props, ref) => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast({
        title: "Ongeldig emailadres",
        description: "Voer een geldig emailadres in.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    // Simulate API call and store in localStorage
    setTimeout(() => {
      const stored = localStorage.getItem("newsletter_emails");
      const emails = stored ? JSON.parse(stored) : [];
      if (!emails.includes(email)) {
        emails.push(email);
        localStorage.setItem("newsletter_emails", JSON.stringify(emails));
      }

      setIsSubmitted(true);
      setIsLoading(false);
      toast({
        title: "Bedankt voor je aanmelding!",
        description: "Je ontvangt binnenkort onze nieuwste deals.",
      });
    }, 500);
  };

  if (isSubmitted) {
    return (
      <div className="flex items-center gap-2 text-primary">
        <Check className="w-5 h-5" />
        <span className="text-sm font-medium">Je bent aangemeld!</span>
      </div>
    );
  }

  return (
    <form ref={ref} onSubmit={handleSubmit} className="space-y-3" {...props}>
      <div className="flex items-center gap-2 text-foreground mb-2">
        <Mail className="w-5 h-5 text-primary" />
        <span className="text-sm font-medium">Blijf op de hoogte van deals</span>
      </div>
      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="je@email.nl"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-secondary border-border"
          required
        />
        <Button type="submit" variant="glow" size="icon" disabled={isLoading} aria-label="Aanmelden nieuwsbrief">
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
});

NewsletterSignup.displayName = "NewsletterSignup";

export default NewsletterSignup;
